import {
  Bell,
  Button,
  CheckCircle,
  CheckSquare,
  Clock,
  Filter,
  Input,
  Search,
  Select,
  Trash2,
  useDebounce,
  Modal,
} from "@ews-admin/global-design-system";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ConfirmationModal } from "../../components/molecules/ConfirmationModal/ConfirmationModal";
import {
  deleteNotificationRequest,
  fetchNotificationsRequest,
  markAllAsReadRequest,
  markAsReadRequest,
} from "../../store/reducers/notificationSlice";
import { RootState } from "../../store/store";
import { toIntlLocale } from "../../utils/format.utils";
import {
  AppNotification,
  NOTIFICATION_TYPES,
} from "../../types/notification.definition";
import {
  getNotificationText,
  getNotificationTypeClasses,
  getNotificationTypeIcon,
} from "../../utils/notification-utils";

const getPriorityColor = (priority?: string) => {
  switch (priority) {
    case "high":
      return "text-error-600 bg-error-50 border-error-200";
    case "medium":
      return "text-warning-600 bg-warning-50 border-warning-200";
    case "low":
      return "text-success-600 bg-success-50 border-success-200";
    default:
      return "text-neutral-600 bg-neutral-50 border-neutral-200";
  }
};

// MED-1653: in the interface language, not the browser's ("Oct 10, 2026, 10:27 PM" in French).
const formatDate = (iso: string | undefined, language: string) => {
  if (!iso) return "";
  return new Date(iso).toLocaleString(toIntlLocale(language), {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

// ─── Notification Detail Modal ────────────────────────────────────────────────

interface NotificationDetailModalProps {
  notification: AppNotification | null;
  onClose: () => void;
}

const NotificationDetailModal: React.FC<NotificationDetailModalProps> = ({
  notification,
  onClose,
}) => {
  const { t, i18n } = useTranslation();
  if (!notification) return null;
  const { title, message } = getNotificationText(notification, t, i18n.language);

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={title}
      variant="info"
      size="md"
      secondaryAction={t("common.close")}
      onSecondaryAction={onClose}
    >
      <p className="mb-3 -mt-2 text-xs font-medium tracking-wide uppercase text-neutral-500">
        {t(`notifications.typeOptions.${notification.type}`, {
          defaultValue: notification.type,
        })}
      </p>

      {/* Body */}
      <div className="space-y-4">
        {/* Priority + Date */}
          <div className="flex items-center gap-3 flex-wrap">
            {notification.priority && (
              <span
                className={`px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(
                  notification.priority
                )}`}
              >
                {t(`notifications.priority.${notification.priority}`, {
                  defaultValue: notification.priority,
                })}
              </span>
            )}
            <div className="flex items-center gap-1 text-xs text-neutral-500">
              <Clock className="w-3.5 h-3.5" />
              <span>{formatDate(notification.createdAt, i18n.language)}</span>
            </div>
          </div>

        {/* Message */}
        <p className="text-sm text-neutral-700 leading-relaxed">
          {message || "—"}
        </p>
      </div>
    </Modal>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

export const Notifications: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const { notifications, loading } = useSelector(
    (state: RootState) => state.notifications
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilter, setSelectedFilter] = useState<"all" | "unread" | "read">("all");
  const [selectedType, setSelectedType] = useState<string>("all");
  const [selectedPriority, setSelectedPriority] = useState<string>("all");
  const [showFilters, setShowFilters] = useState(false);
  const [animateCards, setAnimateCards] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [notificationToDelete, setNotificationToDelete] = useState<AppNotification | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<AppNotification | null>(null);

  const debouncedSearchTerm = useDebounce(searchTerm);

  useEffect(() => {
    dispatch(fetchNotificationsRequest());
    const timer = setTimeout(() => setAnimateCards(true), 100);
    return () => clearTimeout(timer);
  }, [dispatch]);

  // MED-1653: the text shown (translated when the API sent a message_key) is the text searched
  const displayedNotifications = notifications.map((n) => ({
    ...n,
    ...getNotificationText(n, t, i18n.language),
  }));

  const filteredNotifications = displayedNotifications.filter((n) => {
    if (
      debouncedSearchTerm &&
      !n.title.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) &&
      !n.message?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    ) {
      return false;
    }
    if (selectedFilter === "unread" && n.isRead) return false;
    if (selectedFilter === "read" && !n.isRead) return false;
    if (selectedType !== "all" && n.type !== selectedType) return false;
    if (selectedPriority !== "all" && n.priority !== selectedPriority) return false;
    return true;
  });

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const handleNotificationClick = (notification: AppNotification) => {
    if (!notification.isRead) {
      dispatch(markAsReadRequest(notification.id));
    }
    setSelectedNotification(notification);
  };

  const markAllAsRead = () => {
    dispatch(markAllAsReadRequest());
  };

  const handleDeleteClick = (notification: AppNotification) => {
    setNotificationToDelete(notification);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!notificationToDelete) return;
    setIsDeleting(true);
    dispatch(deleteNotificationRequest(notificationToDelete.id));
    setShowDeleteModal(false);
    setNotificationToDelete(null);
    setIsDeleting(false);
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setNotificationToDelete(null);
  };

  return (
    <div className="p-6 space-y-6 animate-fadeIn">
      {/* Header */}
      <div
        className={`transition-all duration-700 ${animateCards ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
      >
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-4">
            <div className="flex justify-center items-center w-12 h-12 rounded-xl bg-ews-primary-100">
              <Bell className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-neutral-900">
                {t("notifications.title")}
              </h1>
              <p className="text-neutral-600">
                {t("notifications.unreadCount", { count: unreadCount })}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              onClick={markAllAsRead}
              disabled={unreadCount === 0 || loading}
              className="hover-scale"
            >
              <CheckSquare className="mr-2 w-4 h-4" />
              {t("notifications.markAllAsRead")}
            </Button>
          </div>
        </div>
      </div>

      {/* Search and Filters — MED-1653: relative z-20 so the filter lists open above the
          notification cards (each card's translate class makes it its own layer). */}
      <div
        className={`relative z-20 bg-white rounded-xl shadow-soft border border-neutral-200 p-4 transition-all duration-700 ${animateCards ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        style={{ transitionDelay: "200ms" }}
      >
        <div className="flex flex-col gap-4 lg:flex-row">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 w-5 h-5 transform -translate-y-1/2 text-neutral-400" />
              <Input
                placeholder={t("notifications.searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
              {searchTerm !== debouncedSearchTerm && searchTerm.trim().length > 0 && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="w-2 h-2 rounded-full animate-pulse bg-primary-500" />
                </div>
              )}
            </div>
            {searchTerm !== debouncedSearchTerm && searchTerm.trim().length > 0 && (
              <p className="mt-1 text-xs text-neutral-500">
                {t("notifications.searching")}
              </p>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              onClick={() => setShowFilters(!showFilters)}
              className="hover-scale"
            >
              <Filter className="mr-2 w-4 h-4" />
              {t("notifications.filters")}
            </Button>
          </div>
        </div>

        {showFilters && (
          <div className="flex items-end gap-4 mt-4 pt-4 border-t border-neutral-200">
            <div className="flex-1 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Select
                label={t("notifications.filterLabel")}
                value={selectedFilter}
                onChange={(value) => setSelectedFilter(value as "all" | "unread" | "read")}
                options={[
                  { value: "all", label: t("notifications.filterOptions.all") },
                  { value: "unread", label: t("notifications.filterOptions.unread") },
                  { value: "read", label: t("notifications.filterOptions.read") },
                ]}
              />
              <Select
                label={t("notifications.typeLabel")}
                value={selectedType}
                onChange={(value) => setSelectedType(value as string)}
                options={[
                  { value: "all", label: t("notifications.typeOptions.all") },
                  ...NOTIFICATION_TYPES.map((type) => ({
                    value: type,
                    label: t(`notifications.typeOptions.${type}`),
                  })),
                ]}
              />
              <Select
                label={t("notifications.priorityLabel")}
                value={selectedPriority}
                onChange={(value) => setSelectedPriority(value as string)}
                options={[
                  { value: "all", label: t("notifications.priorityOptions.all") },
                  { value: "high", label: t("notifications.priorityOptions.high") },
                  { value: "medium", label: t("notifications.priorityOptions.medium") },
                  { value: "low", label: t("notifications.priorityOptions.low") },
                ]}
              />
            </div>
            <Button
              variant="ghost"
              onClick={() => {
                setSearchTerm("");
                setSelectedFilter("all");
                setSelectedType("all");
                setSelectedPriority("all");
              }}
            >
              {t("notifications.clearFilters")}
            </Button>
          </div>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="py-8 text-center text-neutral-500">
          {t("notifications.loading")}
        </div>
      )}

      {/* Notifications List */}
      {!loading && (
        <div className="space-y-4">
          {filteredNotifications.map((notification, index) => {
            const IconComponent = getNotificationTypeIcon(notification.type);
            return (
              <div
                key={notification.id}
                className={`bg-white rounded-xl shadow-soft border border-neutral-200 p-4 sm:p-6 transition-all duration-700 hover:shadow-lg cursor-pointer ${animateCards ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                  } ${!notification.isRead ? "border-l-4 border-l-primary-500" : ""}`}
                style={{ transitionDelay: `${300 + index * 100}ms` }}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex flex-col space-y-4 sm:flex-row sm:items-start sm:space-y-0 sm:space-x-4">
                  {/* Icon */}
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center border flex-shrink-0 ${getNotificationTypeClasses(
                      notification.type
                    )}`}
                  >
                    <IconComponent className="w-6 h-6" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col space-y-3 sm:flex-row sm:items-start sm:justify-between sm:space-y-0">
                      <div className="flex-1">
                        <div className="flex flex-col mb-2 space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-3">
                          <h3
                            className={`text-lg font-semibold ${!notification.isRead ? "text-neutral-900" : "text-neutral-700"
                              }`}
                          >
                            {notification.title}
                          </h3>
                          <div className="flex items-center space-x-2">
                            {!notification.isRead && (
                              <div className="flex-shrink-0 w-2 h-2 rounded-full bg-primary-500" />
                            )}
                            {notification.priority && (
                              <span
                                className={`px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(
                                  notification.priority
                                )}`}
                              >
                                {t(`notifications.priority.${notification.priority}`)}
                              </span>
                            )}
                          </div>
                        </div>
                        <p className="mb-3 text-neutral-600 line-clamp-2">
                          {notification.message}
                        </p>
                        <div className="flex flex-col space-y-2 text-sm sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4 text-neutral-500">
                          <div className="flex items-center space-x-1">
                            <Clock className="w-4 h-4" />
                            <span>{formatDate(notification.createdAt, i18n.language)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div
                        className="flex items-center space-x-2 sm:flex-shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {!notification.isRead && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => dispatch(markAsReadRequest(notification.id))}
                            className="hover-scale"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(notification)}
                          className="text-error-600 hover:text-error-700 hover-scale"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {filteredNotifications.length === 0 && (
            <div className="py-12 text-center">
              <Bell className="mx-auto mb-4 w-16 h-16 text-neutral-400" />
              <h3 className="mb-2 text-lg font-medium text-neutral-900">
                {t("notifications.noNotificationsFound")}
              </h3>
              <p className="text-neutral-600">
                {searchTerm ||
                  selectedFilter !== "all" ||
                  selectedType !== "all" ||
                  selectedPriority !== "all"
                  ? t("notifications.adjustFilters")
                  : t("notifications.allCaughtUp")}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Detail Modal */}
      <NotificationDetailModal
        notification={selectedNotification}
        onClose={() => setSelectedNotification(null)}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        title={t("notifications.deleteModal.title")}
        message={t("notifications.deleteModal.message", {
          title: notificationToDelete?.title,
        })}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  );
};
